import {
  ChangeEvent, FormEvent, useCallback, useState,
} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { PAGE_SEARCH } from 'containers/Search';
import { ConditionalTooltip } from 'shared/components/Conditional';
import * as SearchM from 'shared/models/search';
import useKeypress from 'utils/hooks/keypress';
import { useAuth } from 'utils/hooks/providers/auth-context';
import yup, { ErrorLengthMax, ErrorLengthMin } from 'utils/yup-form-validator';

import './SearchInput.scss';

const schema = yup.object({
  search: yup.string()
    .min(3, ErrorLengthMin(3)).max(48, ErrorLengthMax(48))
    .isAlphanumericHypenBracketColonDot()
    .required(),
});

function SearchInput() {
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const history = useHistory();
  const { authState } = useAuth();
  const urlParams = new URLSearchParams();

  const [timer, setTimer] = useState<any>();

  const setQueryInURLParameters = (value: string) => {
    if (value) {
      urlParams.append('search', value);
    } else {
      urlParams.delete('search');
    }
    history.push(PAGE_SEARCH);
    history.push({ search: urlParams.toString() });
  };

  const clearInput = () => {
    setSearchQuery('');
  };

  useKeypress('Escape', clearInput);

  const placeholderValue = () => {
    let availableSearchElements = [];
    if (authState.organization === '') {
      availableSearchElements = [SearchM.LABEL_SCHC_TEMPLATE];
    } else {
      availableSearchElements = [
        SearchM.LABEL_SCHC_TEMPLATE,
        SearchM.LABEL_SOURCE,
        SearchM.LABEL_DEVICE,
        SearchM.LABEL_IP_NETWORK,
        SearchM.LABEL_FLOW,
      ];
    }
    return `Search for ${availableSearchElements.join('s, ')}s`;
  };

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault(); // Unable page default page reload
      // Check the schema if form is valid:
      const validatedForm = await schema
        .validate({ search: searchQuery }, {
          abortEarly: true, // We just need to return the first occuring error to the user
        })
        .catch((err: Error) => {
          const errorMessage = err.message;
          setError(errorMessage);
          return errorMessage;
        });
      // STOP on Error
      if (typeof validatedForm === 'string') return;
      setError('');

      const run = () => setQueryInURLParameters(searchQuery);

      // -> Submit only on 'enter' key is released
      // Block infinite submit if user keep 'enter pressed'
      clearTimeout(timer);
      const timeoutInMs = 500;
      setTimer(setTimeout(() => run(), timeoutInMs));
    },
    [searchQuery],
  );
  return (
    <ConditionalTooltip title={error} placement="right">
      <form onSubmit={onSubmit} className="search-input">
        <>{ /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }</>
        <label htmlFor="search-field" className={`search-input--icon ${error ? 'search-input--icon--error' : ''}`}>
          <SearchIcon fontSize="small" />
        </label>
        <input
          type="text"
          placeholder={placeholderValue()}
          name="search"
          id="search-field"
          className={`search-input--field ${error ? 'search-input--field--error' : ''}`}
          value={searchQuery}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)}
          autoComplete="off"
        />
        {
          searchQuery.length
            ? (
              <IconButton size="small" onClick={clearInput}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            )
            : null
        }
        <input type="submit" style={{ display: 'none' }} />
      </form>
    </ConditionalTooltip>
  );
}

export default SearchInput;
